import { ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/Button';

interface ModuleCardProps {
  id: string;
  number: string;
  name: string;
  description: string;
  newsletterTitle: string;
  newsletterUrl: string;
}

const modules: ModuleCardProps[] = [
  {
    id: 'signal-architecture',
    number: '01',
    name: 'Signal Architecture',
    description:
      'Most retention teams react to problems that surfaced weeks ago. Signal architecture is the system for detecting risk early — defining which behavioural signals actually predict churn in your specific business, and building the infrastructure to monitor them continuously.',
    newsletterTitle: 'Building a Signal Architecture That Actually Predicts Churn',
    newsletterUrl: 'https://churnlogix.substack.com',
  },
  {
    id: 'risk-categories',
    number: '02',
    name: 'Risk Categories',
    description:
      'At-risk is not a diagnosis. It tells you a client might churn and nothing else. Risk categories break that label into specific failure modes — onboarding breakdown, value recognition gaps, executive misalignment, product risk, payment friction — each of which requires a completely different response.',
    newsletterTitle: 'Why "At-Risk" Is Not a Useful Label',
    newsletterUrl: 'https://churnlogix.substack.com',
  },
  {
    id: 'intervention-playbooks',
    number: '03',
    name: 'Intervention Playbooks',
    description:
      'The right intervention depends entirely on the failure mode. A playbook is not a check-in call. It is a specific sequence of actions, owned by a specific person, targeting a specific root cause. This module documents the exact response for each risk category in your business.',
    newsletterTitle: 'Playbooks That Actually Get Executed',
    newsletterUrl: 'https://churnlogix.substack.com',
  },
  {
    id: 'leading-metrics',
    number: '04',
    name: 'Leading Metrics',
    description:
      'Most retention metrics confirm what already happened. Leading metrics track the behavioural shifts happening right now, before they show up in health scores or usage drops. This module defines your specific thresholds and calibrates them from your own churn data.',
    newsletterTitle: 'The Metrics That Actually Predict Outcomes',
    newsletterUrl: 'https://churnlogix.substack.com',
  },
  {
    id: 'renewal-process',
    number: '05',
    name: 'Renewal Process',
    description:
      'If retention work starts at renewal, it is damage control not retention. This module maps what needs to be resolved at 180 days, 90 days, and 60 days before renewal so the renewal conversation is a confirmation, not a rescue operation.',
    newsletterTitle: 'Renewals Should Be Confirmations, Not Negotiations',
    newsletterUrl: 'https://churnlogix.substack.com',
  },
];

function ModuleCard({ id, number, name, description, newsletterTitle, newsletterUrl }: ModuleCardProps) {
  return (
    <div
      id={id}
      className="bg-white dark:bg-obsidian-50 border border-slate-200 dark:border-slate-700 rounded p-6 md:p-8 scroll-mt-24"
    >
      <div className="flex items-start gap-4 md:gap-6">
        <span className="text-4xl md:text-5xl font-heading font-black text-amber flex-shrink-0">
          {number}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl md:text-2xl font-heading font-black text-obsidian dark:text-signal">
            {name}
          </h3>
          <p className="mt-3 text-slate-600 dark:text-slate-400 leading-relaxed">
            {description}
          </p>
          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-500 mb-2">
              Covered in the newsletter:
            </p>
            <a
              href={newsletterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-amber hover:underline font-medium"
            >
              {newsletterTitle}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="mt-6">
            <a href="https://app.churnlogix.com" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Build this module
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HowItWorksPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="section-padding bg-slate-50 dark:bg-obsidian">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-heading font-black text-obsidian dark:text-signal leading-tight">
              Where the newsletter and{' '}
              <span className="text-amber">the tool connect.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              The ChurnLogix newsletter covers the thinking behind retention systems. The tool builds them. How It Works is where the two connect — each module explained in depth so you understand what you're building and why.
            </p>
          </div>
        </div>
      </section>

      {/* Module Cards */}
      <section className="section-padding bg-white dark:bg-obsidian border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto space-y-6">
            {modules.map((module) => (
              <ModuleCard key={module.id} {...module} />
            ))}
          </div>
        </div>
      </section>

      {/* More Modules Coming */}
      <section className="section-padding bg-slate-100 dark:bg-[#0F1014]">
        <div className="container-custom text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-black text-obsidian dark:text-signal">
              More modules are coming
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-400 leading-relaxed">
              More modules are being added as the framework develops. Subscribe to the newsletter to follow along as each new component is documented and added to the tool.
            </p>
            <div className="mt-8">
              <a href="https://churnlogix.substack.com" target="_blank" rel="noopener noreferrer">
                <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                  Subscribe to the newsletter
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HowItWorksPage;
